import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiSearch, FiTrendingUp, FiBookOpen, FiFileText, FiUsers, FiDollarSign, FiBarChart2, FiShield, FiGlobe, FiSettings, FiArrowRight } from 'react-icons/fi';

const ALL_SERVICES = [
  {
    title: 'Audit Services',
    icon: FiSearch,
    description: 'Comprehensive review to ensure that the accounts are prepared in accordance with Generally Accepted Accounting Policies and applicable Accounting Standards/IFRS. Checking the genuineness of the expenses booked in accounts.',
    link: '/services/audit',
    tags: ['Internal Audit', 'Statutory Audit', 'IFRS', 'Accounting Policies'],
  },
  {
    title: 'Corporate Services',
    icon: FiTrendingUp,
    description: 'Planning for Mergers, Acquisitions, De-mergers, and Corporate re-organizations. Filing of annual returns and various forms, documents. Consultancy on Company Law matters.',
    link: '/services/corporate-services',
    tags: ['Mergers & Acquisitions', 'Annual Returns', 'Company Law'],
  },
  {
    title: 'Accounting Services',
    icon: FiBookOpen,
    description: 'Accounting System Design & Implementation. Financial Accounting. Budgeting. We provide complete bookkeeping and financial record management tailored to your business needs.',
    link: '/services/accounting',
    tags: ['Bookkeeping', 'Financial Accounting', 'Budgeting', 'System Design'],
  },
  {
    title: 'GST Services',
    icon: FiFileText,
    description: 'Filing of GST Returns (Regular Dealer, Composition Dealer, ISD, Non-Resident, E-Commerce, etc). GST Consultancy/Advisory on various issues of GST.',
    link: '/services/gst',
    tags: ['GST Filing', 'GSTR', 'E-Commerce GST', 'Advisory'],
  },
  {
    title: 'Income Tax',
    icon: FiDollarSign,
    description: 'Comprehensive income tax planning, filing and advisory services for individuals, firms and corporates. Tax optimization strategies for maximum savings within legal bounds.',
    link: '/services/income-tax',
    tags: ['Tax Planning', 'ITR Filing', 'Tax Advisory'],
  },
  {
    title: 'Corporate Finance',
    icon: FiBarChart2,
    description: 'Loan Financing, Project Financing, Business Consultancy Services. We assist businesses in raising capital and structuring optimal financial solutions.',
    link: '/services/corporate-finance',
    tags: ['Loan Financing', 'Project Finance', 'Capital Structuring'],
  },
  {
    title: 'Services for Non-Residents',
    icon: FiGlobe,
    description: 'Specialized financial and compliance services for Non-Resident Indians (NRIs), including FEMA compliance, foreign remittances, and cross-border tax advisory.',
    link: '/services/non-residents',
    tags: ['NRI Services', 'FEMA', 'Foreign Remittances'],
  },
  {
    title: 'Payroll Services',
    icon: FiUsers,
    description: 'Complete payroll management including salary computations, TDS on salaries, PF/ESI compliance, and generation of payslips and Form 16.',
    link: '/services/payroll',
    tags: ['Salary Processing', 'TDS on Salary', 'Form 16', 'PF/ESI'],
  },
  {
    title: 'TDS Services',
    icon: FiSettings,
    description: 'TDS compliance and advisory, including computation, deposit and return filing for TDS and fringe benefit tax matters.',
    link: '/services/tds',
    tags: ['TDS Computation', 'TDS Returns', 'TCS', 'Fringe Benefit'],
  },
  {
    title: 'Corporate Governance',
    icon: FiShield,
    description: 'Advisory on Corporate Governance best practices, compliance with Secretarial Standards, Board meeting compliance, and preparation of annual reports.',
    link: '/services/corporate-governance',
    tags: ['Board Compliance', 'Secretarial Standards', 'Annual Report'],
  },
  {
    title: 'Benefits of Outsourcing',
    icon: FiTrendingUp,
    description: 'Discover the strategic advantages of outsourcing your accounting, payroll and compliance functions to a trusted professional firm — reduce costs and focus on core business.',
    link: '/services/outsourcing',
    tags: ['Cost Reduction', 'Compliance Outsourcing', 'BPO'],
  },
];

export default function ServicesPage() {
  const [active, setActive] = useState(null);

  return (
    <div className="page-wrapper">
      <div className="page-hero">
        <h1>Our <span>Services</span></h1>
        <p>Comprehensive financial, tax & advisory solutions for every business need</p>
      </div>

      <main className="services-page-main">
        <div className="container">
          {/* Intro */}
          <div className="services-page-intro">

            <p className="services-page-intro-text">
              From audit and accounting to GST, income tax, and corporate advisory — ASRM & Co. brings 30+ years of expertise to every engagement. We serve individuals, startups, SMEs and large corporates across diverse industries.
            </p>
          </div>

          {/* Services grid */}
          <div className="services-page-grid">
            {ALL_SERVICES.map((svc, i) => {
              const Icon = svc.icon;
              const isOpen = active === i;
              return (
                <div
                  key={svc.title}
                  className={`services-page-card ${isOpen ? 'services-page-card-active' : ''}`}
                  onClick={() => setActive(isOpen ? null : i)}
                >
                  <div className="services-page-card-header">
                    <div className="services-page-icon-box">
                      <Icon size={24} />
                    </div>
                    <div>
                      <h3 className="services-page-card-title">{svc.title}</h3>
                      <div className="services-page-tags">
                        {svc.tags.slice(0, 2).map((t) => (
                          <span key={t} className="services-page-tag">{t}</span>
                        ))}
                      </div>
                    </div>
                  </div>

                  <p className={`services-page-card-desc ${isOpen ? 'services-page-desc-open' : ''}`}>
                    {svc.description}
                  </p>

                  <Link
                    to={svc.link}
                    className="services-page-learn-more"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Learn More <FiArrowRight size={13} />
                  </Link>
                </div>
              );
            })}
          </div>

          {/* Industries served */}
          <div className="services-page-industries">
            <h3 className="services-page-ind-title">Industries We Serve</h3>
            <div className="services-page-ind-grid">
              {[
                'Sponge Iron', 'Aluminium', 'Rice Mill', 'Chemical Manufacturing',
                'Startups', 'Transporting', 'Diagnostic & Hospital', 'BPO',
                'Stock Broking', 'Commodity Exchanges', 'Carton Industries', 'Real Estate',
              ].map((ind) => (
                <div key={ind} className="services-page-ind-item">
                  <span className="services-page-ind-dot" />
                  {ind}
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
